/**
 * <hairstory-returns-form> — self-serve returns form web component.
 *
 * Drop into any Shopify section/page:
 *   <script src="https://hairstory-backend-xi.vercel.app/returns-form.js" defer></script>
 *   <hairstory-returns-form></hairstory-returns-form>
 *
 * Optional attribute:
 *   api-base="https://hairstory-backend-xi.vercel.app"  (defaults to that)
 *
 * Vanilla JS/CSS/HTML, light DOM (no shadow DOM) — all classes are prefixed
 * with hrf- to avoid colliding with theme styles.
 */
(function () {
  'use strict';

  var DEFAULT_API_BASE = 'https://hairstory-backend-xi.vercel.app';

  var REASONS = [
    { value: 'damaged', label: 'My item arrived damaged' },
    { value: 'wrong_item', label: 'I received the wrong item' },
    { value: 'too_much_product', label: 'I have too much product (order unopened)' },
    { value: 'not_satisfied', label: "I'm not satisfied with a product" },
  ];

  var PHOTO_NOTES = {
    damaged:
      'a photo of the damaged item along with its batch number (printed on the bottle or box)',
    wrong_item: 'a photo of the item you received',
    too_much_product: 'a photo of the sealed, unopened item',
  };

  var RESOLUTIONS = [
    {
      value: 'keep_with_coupon',
      label: 'Keep it and get a 20% off coupon',
      detail: 'No need to ship anything back. Choosing this forfeits the return.',
    },
    {
      value: 'exchange_with_coupon',
      label: 'Exchange it and get a 10% off coupon',
      detail: 'Swap for a product that suits you better. Choosing this forfeits the refund.',
    },
    {
      value: 'refund',
      label: 'Return it for a refund',
      detail: 'Our team will follow up with return instructions.',
    },
  ];

  var QUIZ_QUESTIONS = [
    {
      key: 'issues',
      label: 'What issue are you having with the product? (select all that apply)',
      type: 'multi',
      options: ['Greasy', 'Flaky', 'Dry', 'Breakage', 'Residue', 'Other'],
    },
    {
      key: 'weeksUsing',
      label: 'How many weeks have you been using New Wash?',
      type: 'select',
      options: ['Less than 1 week', '1–2 weeks', '3–4 weeks', '1–2 months', 'More than 2 months'],
    },
    {
      key: 'tablespoons',
      label: 'Around how many tablespoons of New Wash are you using when you wash?',
      type: 'select',
      options: ['Less than 1', '1', '2', 'More than 2'],
    },
    {
      key: 'scalpDescription',
      label: 'How would you describe your scalp? (oily / flaky / sensitive / no issues)',
      type: 'text',
    },
    {
      key: 'oilyTiming',
      label:
        'Does your hair feel oily immediately after washing, within a few hours, or the next day?',
      type: 'text',
    },
    {
      key: 'hardWater',
      label: 'Do you live in a hard water area?',
      type: 'select',
      options: ['Yes', 'No', 'Yes, with a whole-home water softener/filter', 'Yes, but only a shower-head filter', 'Not sure'],
    },
    {
      key: 'hairTexture',
      label: 'What is your hair texture?',
      type: 'select',
      options: [
        'Fine: Thinner than a strand of thread',
        'Normal: As thick as a strand of thread',
        'Thick or Coarse: Thicker than a strand of thread',
      ],
    },
    {
      key: 'hairType',
      label: 'What is your natural hair type?',
      type: 'select',
      options: [
        'Straight: No bend or wave',
        'Wavy: Naturally has a loose S-shape',
        'Curly: Defined spirals with lots of bounce',
        'Coily: Tightly coiled strands',
      ],
    },
    {
      key: 'hairLength',
      label: 'How long is your hair?',
      type: 'select',
      options: [
        'Super short (scalp)',
        'Short (ears)',
        'Medium (chin, shoulders)',
        'Long (chest or longer)',
      ],
    },
  ];

  // Matches hairstory.com: Barlow body / Barlow Condensed headings (both loaded
  // by the theme), #2b2b2b monochrome, square corners, uppercase CTAs.
  var STYLES = [
    'hairstory-returns-form{display:block;max-width:560px;margin:0 auto;font-family:Barlow,sans-serif;font-size:16px;letter-spacing:.5px;line-height:1.5;color:#2b2b2b}',
    'hairstory-returns-form *{box-sizing:border-box}',
    '.hrf-step{margin-bottom:28px}',
    '.hrf-heading{font-family:"Barlow Condensed",Barlow,sans-serif;font-size:1.5em;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin:24px 0 12px}',
    '.hrf-step>.hrf-heading:first-child{margin-top:0}',
    '.hrf-label{display:block;font-size:.75em;font-weight:500;text-transform:uppercase;letter-spacing:1px;margin:16px 0 6px}',
    '.hrf-input,.hrf-select,.hrf-textarea{width:100%;padding:12px 14px;border:1px solid #2b2b2b;border-radius:0;font-family:inherit;font-size:16px;letter-spacing:.5px;background:#fff;color:#2b2b2b}',
    '.hrf-input:focus,.hrf-select:focus,.hrf-textarea:focus{outline:2px solid #2b2b2b;outline-offset:-1px}',
    '.hrf-textarea{min-height:80px;resize:vertical}',
    '.hrf-option{display:flex;align-items:flex-start;gap:10px;padding:14px;border:1px solid #d6d6d2;border-radius:0;margin-bottom:8px;cursor:pointer;background:#fff}',
    '.hrf-option:hover{border-color:#2b2b2b}',
    '.hrf-option:has(input:checked){border-color:#2b2b2b;background:#f4f4f2}',
    '.hrf-option input{margin-top:4px;flex-shrink:0;accent-color:#2b2b2b}',
    '.hrf-option-detail{display:block;font-size:.85em;color:#6b6b6b;margin-top:2px}',
    '.hrf-notice{background:#f4f4f2;border:1px solid #2b2b2b;border-radius:0;padding:12px 14px;margin:16px 0;font-size:.95em}',
    '.hrf-tip{background:#f4f4f2;border-radius:0;padding:12px 14px;margin:8px 0;font-size:.95em}',
    '.hrf-tip strong{display:block;margin-bottom:4px;font-weight:600;text-transform:uppercase;font-size:.8em;letter-spacing:1px}',
    '.hrf-error{color:#b3261e;font-size:.9em;margin:8px 0}',
    '.hrf-btn{display:inline-block;padding:14px 32px;border:1px solid #2b2b2b;border-radius:0;background:#2b2b2b;color:#fff;font-family:inherit;font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;cursor:pointer;margin-top:8px}',
    '.hrf-btn:hover{background:#000;border-color:#000}',
    '.hrf-btn:disabled{background:#999;border-color:#999;cursor:default}',
    '.hrf-btn-link{background:none;border:none;color:#6b6b6b;font-family:inherit;font-size:12px;font-weight:500;letter-spacing:1px;text-transform:uppercase;text-decoration:underline;cursor:pointer;padding:0;display:inline-block}',
    '.hrf-btn-link:hover{color:#2b2b2b}',
    '.hrf-actions{display:flex;flex-direction:column;align-items:flex-start;gap:20px;margin-top:28px}',
    '.hrf-actions-secondary{display:flex;flex-direction:column;align-items:flex-start;gap:10px}',
    '.hrf-actions .hrf-btn{margin:0}',
    '.hrf-withdrawal{border:1px solid #2b2b2b;background:#f4f4f2;padding:16px;margin:0 0 28px}',
    '.hrf-withdrawal h4{font-family:"Barlow Condensed",Barlow,sans-serif;font-size:1.2em;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin:0 0 6px}',
    '.hrf-withdrawal p{margin:0 0 14px;font-size:.95em}',
    '.hrf-withdrawal .hrf-btn{margin:0}',
    '.hrf-summary{background:#f4f4f2;padding:16px;margin:8px 0}',
    '.hrf-summary-label{font-size:.7em;font-weight:500;text-transform:uppercase;letter-spacing:1px;color:#6b6b6b;margin-top:12px}',
    '.hrf-summary>.hrf-summary-label:first-child{margin-top:0}',
    '.hrf-summary-value{margin-top:2px}',
    '.hrf-success{text-align:center;padding:32px 16px}',
    '.hrf-success h3{font-family:"Barlow Condensed",Barlow,sans-serif;font-size:2em;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin:0 0 12px}',
    '.hrf-quiz-q{margin-bottom:16px}',
  ].join('\n');

  var MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
  var MAX_DIMENSION = 2000;
  var JPEG_QUALITY = 0.85;

  /**
   * Downscale to MAX_DIMENSION and re-encode as JPEG before upload.
   * Keeps iPhone photos (often 8-15MB as converted JPEG, HEIC unviewable in
   * Gorgias) well under the 10MB bucket cap and always viewable by CX.
   * Resolves to the original file if the image can't be decoded (e.g. HEIC on
   * a non-Safari browser) and it's within the size cap; rejects only when the
   * undecodable original is over the cap.
   */
  function compressImage(file) {
    return decodeImage(file)
      .then(function (source) {
        var scale = Math.min(1, MAX_DIMENSION / Math.max(source.width, source.height));
        var canvas = document.createElement('canvas');
        canvas.width = Math.round(source.width * scale);
        canvas.height = Math.round(source.height * scale);
        canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
        if (source.close) source.close();
        return new Promise(function (resolve, reject) {
          canvas.toBlob(function (blob) {
            if (blob && blob.size > 0) {
              resolve(new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
            } else {
              reject(new Error('encode failed'));
            }
          }, 'image/jpeg', JPEG_QUALITY);
        });
      })
      .catch(function () {
        if (file.size <= MAX_UPLOAD_BYTES) return file;
        throw new Error('too large');
      });
  }

  function decodeImage(file) {
    if (window.createImageBitmap) {
      return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () {
        return decodeViaImageElement(file);
      });
    }
    return decodeViaImageElement(file);
  }

  function decodeViaImageElement(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve(img); URL.revokeObjectURL(url); };
      img.onerror = function () { reject(new Error('decode failed')); URL.revokeObjectURL(url); };
      img.src = url;
    });
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'text') node.textContent = attrs[k];
        else if (k === 'html') node.innerHTML = attrs[k];
        else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2), attrs[k]);
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c) node.appendChild(c);
    });
    return node;
  }

  class HairstoryReturnsForm extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.apiBase = (this.getAttribute('api-base') || DEFAULT_API_BASE).replace(/\/$/, '');
      // Set by the Shopify section via Liquid when the visitor is in an EU-27
      // country — drives the clearly-visible up-front withdrawal function the
      // EU right-of-withdrawal rule requires.
      this.euRegion = this.getAttribute('eu-region') === 'true';
      this.state = {
        reason: null,
        email: '',
        orderNumber: '',
        order: null, // lookup result
        selected: {}, // item index -> true (not_satisfied)
        withdrawal: { name: '', selected: {} }, // EU right of withdrawal (step 2 item selection)
        busy: false,
        error: null,
      };
      if (!document.getElementById('hrf-styles')) {
        var style = document.createElement('style');
        style.id = 'hrf-styles';
        style.textContent = STYLES;
        document.head.appendChild(style);
      }
      this.renderLookupStep();
    }

    // ---------- Step 2: reason ----------

    renderReasonStep() {
      var self = this;
      var order = this.state.order;
      this.innerHTML = '';
      var step = el('div', { class: 'hrf-step' }, [
        el('p', { text: 'Order ' + order.orderName }),
      ]);

      // EU right of withdrawal — a distinct, clearly-visible cancellation
      // function, shown only for EU orders still within the 14-day window.
      if (order.isEU && order.withdrawalWindowOpen) {
        var wd = el('div', { class: 'hrf-withdrawal' }, [
          el('h4', { text: 'Change your mind? Cancel your order' }),
          el('p', {
            text:
              'As an EU customer, you can withdraw from your purchase within 14 days of delivery, no reason needed. The items must be unopened and in their original sealed packaging. If you’ve opened an item, choose a return reason below instead.',
          }),
          el('button', {
            class: 'hrf-btn',
            type: 'button',
            text: 'Cancel my order',
            onclick: function () {
              self.state.reason = 'withdrawal';
              self.state.error = null;
              self.state.withdrawalEntry = 'reason';
              self.renderWithdrawalStep();
            },
          }),
        ]);
        step.appendChild(wd);
      }

      step.appendChild(el('h3', { class: 'hrf-heading', text: 'Why would you like to return?' }));
      REASONS.forEach(function (r) {
        step.appendChild(
          el('label', { class: 'hrf-option' }, [
            el('input', {
              type: 'radio',
              name: 'hrf-reason',
              value: r.value,
              onchange: function () {
                self.state.reason = r.value;
                self.state.error = null;
                if (r.value === 'not_satisfied') {
                  self.state.selected = {};
                  self.renderItemsStep();
                } else {
                  self.renderPhotoReasonStep();
                }
              },
            }),
            el('span', { text: r.label }),
          ])
        );
      });
      step.appendChild(
        el('div', { class: 'hrf-actions' }, [
          this.backLink(function () { self.renderLookupStep(); }),
        ])
      );
      this.appendChild(step);
    }

    // ---------- EU right of withdrawal (step 2: select items + submit) ----------

    renderWithdrawalStep() {
      var self = this;
      var order = this.state.order;
      var wd = this.state.withdrawal;
      this.innerHTML = '';
      var step = el('div', { class: 'hrf-step' }, [
        el('h3', { class: 'hrf-heading', text: 'Cancel your order' }),
        el('p', { text: 'Order ' + order.orderName }),
      ]);

      step.appendChild(el('label', { class: 'hrf-label', text: 'Which items would you like to cancel?' }));
      order.items.forEach(function (item, i) {
        if (!(i in wd.selected)) wd.selected[i] = true; // default all selected
        var cb = el('input', {
          type: 'checkbox',
          value: String(i),
          onchange: function (e) { wd.selected[i] = e.target.checked; },
        });
        cb.checked = wd.selected[i];
        step.appendChild(
          el('label', { class: 'hrf-option' }, [
            cb,
            el('span', { text: item.title + (item.quantity > 1 ? ' × ' + item.quantity : '') }),
          ])
        );
      });

      step.appendChild(
        el('div', {
          class: 'hrf-notice',
          text:
            'Only items that are still unopened and in their original sealed packaging qualify for withdrawal. If you’ve opened an item, please go back and choose a return reason instead.',
        })
      );

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-name', text: 'Your full name (optional)' }));
      var name = el('input', { class: 'hrf-input', id: 'hrf-name', type: 'text', placeholder: 'First and last name', value: wd.name });
      step.appendChild(name);

      var errorBox = el('div', { class: 'hrf-error' });
      step.appendChild(errorBox);

      var submitBtn = el('button', {
        class: 'hrf-btn',
        type: 'button',
        text: 'Submit cancellation',
        onclick: function () {
          errorBox.textContent = '';
          var items = self.getWithdrawalItems();
          if (items.length === 0) {
            errorBox.textContent = 'Please select at least one item to cancel.';
            return;
          }
          wd.name = name.value.trim();
          self.submit(
            {
              email: self.state.email,
              reason: 'withdrawal',
              orderNumber: order.orderName,
              customerName: wd.name || undefined,
              items: items.map(function (item) {
                return { title: item.title, quantity: item.quantity, category: item.category };
              }),
            },
            submitBtn,
            errorBox
          );
        },
      });

      var backFn =
        self.state.withdrawalEntry === 'lookup'
          ? function () { self.renderLookupStep({ withdrawal: true }); }
          : function () { self.renderReasonStep(); };
      step.appendChild(this.actions(submitBtn, [this.backLink(backFn)]));
      this.appendChild(step);
    }

    getWithdrawalItems() {
      var self = this;
      return this.state.order.items.filter(function (_, i) { return self.state.withdrawal.selected[i]; });
    }

    // ---------- Damaged / wrong item / too much product ----------

    renderPhotoReasonStep() {
      var self = this;
      var reason = this.state.reason;
      this.innerHTML = '';
      var step = el('div', { class: 'hrf-step' }, [
        el('h3', { class: 'hrf-heading', text: REASONS.find(function (r) { return r.value === reason; }).label }),
        el('p', { text: 'Order ' + this.state.order.orderName }),
      ]);

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-photo', text: 'Attach ' + PHOTO_NOTES[reason] }));
      var photo = el('input', { class: 'hrf-input', id: 'hrf-photo', type: 'file', accept: 'image/*' });
      step.appendChild(photo);

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-comments', text: 'Anything else we should know? (optional)' }));
      var comments = el('textarea', { class: 'hrf-textarea', id: 'hrf-comments' });
      step.appendChild(comments);

      var errorBox = el('div', { class: 'hrf-error' });
      step.appendChild(errorBox);

      var skipPhoto = false;

      function doSubmit() {
        errorBox.textContent = '';
        var file = photo.files && photo.files[0];
        if (!file && !skipPhoto) {
          errorBox.textContent = 'Please attach a photo, or choose “submit without a photo” below.';
          return;
        }

        var payload = {
          email: self.state.email,
          reason: reason,
          orderNumber: self.state.order.orderName,
          comments: comments.value.trim() || undefined,
        };

        if (!file || skipPhoto) {
          self.submit(payload, submitBtn, errorBox);
          return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = 'Uploading photo…';
        compressImage(file)
          .then(function (upload) {
            var formData = new FormData();
            formData.append('photo', upload, upload.name || 'photo.jpg');
            return fetch(self.apiBase + '/api/returns/upload', { method: 'POST', body: formData });
          })
          .then(function (res) {
            return res.json().then(function (data) { return { status: res.status, data: data }; });
          })
          .then(function (result) {
            if (result.status === 200) {
              payload.photoPath = result.data.path;
              self.submit(payload, submitBtn, errorBox);
            } else if (result.status === 503) {
              // Uploads not enabled — submit anyway; success screen + ticket
              // tell the customer to email the photo instead.
              self.submit(payload, submitBtn, errorBox);
            } else if (result.status === 400) {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Submit request';
              errorBox.textContent = result.data.error || 'That photo can’t be uploaded. Please try a different one.';
            } else {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Submit request';
              errorBox.textContent = 'We couldn’t upload your photo. Please try again, or submit without it below.';
            }
          })
          .catch(function (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit request';
            errorBox.textContent =
              err && err.message === 'too large'
                ? 'That photo is too large. Please choose a smaller one, or submit without it below.'
                : 'We couldn’t upload your photo. Please try again, or submit without it below.';
          });
      }

      var submitBtn = el('button', { class: 'hrf-btn', type: 'button', text: 'Submit request', onclick: doSubmit });

      var skipLink = el('button', {
        class: 'hrf-btn-link',
        type: 'button',
        text: 'Can’t attach a photo? Submit without one',
        onclick: function () {
          skipPhoto = true;
          photo.value = '';
          doSubmit();
        },
      });

      step.appendChild(this.actions(submitBtn, [skipLink, this.backLink()]));
      this.appendChild(step);
    }

    // ---------- Step 1: find your order ----------

    // opts.withdrawal: this lookup is the entry to the EU withdrawal flow, so on
    // success route to the withdrawal item-selection step (only if eligible).
    renderLookupStep(opts) {
      var self = this;
      var withdrawal = !!(opts && opts.withdrawal);
      this.innerHTML = '';
      var step = el('div', { class: 'hrf-step' }, [
        el('h3', { class: 'hrf-heading', text: withdrawal ? 'Withdraw from your order' : 'Find your order' }),
      ]);

      // EU right of withdrawal — a clearly-visible, directly-accessible
      // withdrawal function shown up front to EU-region visitors (per the
      // 2026 EU rule). Non-EU visitors still reach it via the reason step if
      // their order turns out to ship to the EU.
      if (!withdrawal && this.euRegion) {
        step.appendChild(
          el('div', { class: 'hrf-withdrawal' }, [
            el('h4', { text: 'EU customer? Cancel your order' }),
            el('p', {
              text:
                'Withdraw from your purchase within 14 days of delivery, no reason needed. The items must be unopened and in their original sealed packaging. If you’ve opened an item, use the regular return process below instead.',
            }),
            el('button', {
              class: 'hrf-btn',
              type: 'button',
              text: 'Withdraw from your order',
              onclick: function () { self.renderLookupStep({ withdrawal: true }); },
            }),
          ])
        );
        step.appendChild(el('h3', { class: 'hrf-heading', text: 'Returning something else?' }));
      }

      if (withdrawal) {
        step.appendChild(
          el('p', { text: 'Find your order to start your cancellation. You’ll choose what to cancel on the next step.' })
        );
      }

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-email', text: 'Your email' }));
      var email = el('input', { class: 'hrf-input', id: 'hrf-email', type: 'email', placeholder: 'you@example.com', value: this.state.email });
      step.appendChild(email);

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-order', text: 'Order number' }));
      var order = el('input', { class: 'hrf-input', id: 'hrf-order', type: 'text', placeholder: 'e.g. 123456', value: this.state.orderNumber });
      step.appendChild(order);

      var errorBox = el('div', { class: 'hrf-error' });
      step.appendChild(errorBox);

      var findLabel = withdrawal ? 'Continue' : 'Find my order';
      var findBtn = el('button', {
        class: 'hrf-btn',
        type: 'button',
        text: findLabel,
        onclick: function () {
          errorBox.textContent = '';
          var emailVal = email.value.trim();
          var orderVal = order.value.trim();
          if (!emailVal || emailVal.indexOf('@') === -1) {
            errorBox.textContent = 'Please enter a valid email address.';
            return;
          }
          if (!orderVal) {
            errorBox.textContent = 'Please enter your order number.';
            return;
          }
          findBtn.disabled = true;
          findBtn.textContent = 'Looking up…';
          fetch(self.apiBase + '/api/returns/lookup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailVal, orderNumber: orderVal }),
          })
            .then(function (res) {
              return res.json().then(function (data) { return { ok: res.ok, data: data }; });
            })
            .then(function (result) {
              findBtn.disabled = false;
              findBtn.textContent = findLabel;
              if (!result.ok) {
                errorBox.textContent =
                  result.data.error || 'We couldn’t find that order. Please double-check the order number and email.';
                return;
              }
              self.state.email = emailVal;
              self.state.orderNumber = orderVal;
              self.state.order = result.data.order;
              self.state.withdrawal = { name: '', selected: {} };
              if (withdrawal) {
                if (result.data.order.isEU && result.data.order.withdrawalWindowOpen) {
                  self.state.withdrawalEntry = 'lookup';
                  self.renderWithdrawalStep();
                } else {
                  errorBox.textContent =
                    'This order isn’t eligible for the right of withdrawal. It may be outside the 14-day window or not shipped within the EU. To return it, go back and choose a return reason, or contact us.';
                }
                return;
              }
              self.renderReasonStep();
            })
            .catch(function () {
              findBtn.disabled = false;
              findBtn.textContent = findLabel;
              errorBox.textContent = 'Something went wrong. Please try again.';
            });
        },
      });

      var secondary = withdrawal ? [this.backLink(function () { self.renderLookupStep(); })] : [];
      step.appendChild(this.actions(findBtn, secondary));
      this.appendChild(step);
    }

    // ---------- Not satisfied: item selection + tips/offers/quiz ----------

    renderItemsStep() {
      var self = this;
      var order = this.state.order;
      this.innerHTML = '';
      var step = el('div', { class: 'hrf-step' }, [
        el('h3', { class: 'hrf-heading', text: 'Which items would you like to return?' }),
        el('p', { text: 'Order ' + order.orderName }),
      ]);

      var dynamicArea = el('div', {});

      order.items.forEach(function (item, i) {
        step.appendChild(
          el('label', { class: 'hrf-option' }, [
            el('input', {
              type: 'checkbox',
              value: String(i),
              onchange: function (e) {
                self.state.selected[i] = e.target.checked;
                self.renderDynamicArea(dynamicArea);
              },
            }),
            el('span', { text: item.title + (item.quantity > 1 ? ' × ' + item.quantity : '') }),
          ])
        );
      });

      step.appendChild(dynamicArea);

      step.appendChild(el('label', { class: 'hrf-label', for: 'hrf-comments', text: 'Anything else we should know? (optional)' }));
      var comments = el('textarea', { class: 'hrf-textarea', id: 'hrf-comments' });
      step.appendChild(comments);

      var errorBox = el('div', { class: 'hrf-error' });
      step.appendChild(errorBox);

      var submitBtn = el('button', {
        class: 'hrf-btn',
        type: 'button',
        text: 'Submit request',
        onclick: function () {
          errorBox.textContent = '';
          var selectedItems = self.getSelectedItems();
          if (selectedItems.length === 0) {
            errorBox.textContent = 'Please select at least one item.';
            return;
          }
          var payload = {
            email: self.state.email,
            reason: 'not_satisfied',
            orderNumber: self.state.order.orderName,
            comments: comments.value.trim() || undefined,
            items: selectedItems.map(function (item) {
              return { title: item.title, quantity: item.quantity, category: item.category };
            }),
          };

          var needsResolution = selectedItems.some(function (item) {
            return item.category === 'styling' || item.category === 'treatment' || item.category === 'other';
          });
          if (needsResolution) {
            var chosen = self.querySelector('input[name="hrf-resolution"]:checked');
            if (!chosen) {
              errorBox.textContent = 'Please choose how you’d like us to resolve this.';
              return;
            }
            payload.resolution = chosen.value;
          }

          var needsQuiz = selectedItems.some(function (item) { return item.category === 'washing'; });
          if (needsQuiz) {
            var quiz = {};
            for (var q = 0; q < QUIZ_QUESTIONS.length; q++) {
              var question = QUIZ_QUESTIONS[q];
              var val = '';
              if (question.type === 'multi') {
                var checked = self.querySelectorAll(
                  'input[name="hrf-quiz-' + question.key + '"]:checked'
                );
                var vals = [];
                for (var c = 0; c < checked.length; c++) vals.push(checked[c].value);
                val = vals.join(', ');
              } else {
                var field = self.querySelector('[name="hrf-quiz-' + question.key + '"]');
                val = field ? field.value.trim() : '';
              }
              if (!val) {
                errorBox.textContent = 'Please answer all the New Wash questions.';
                return;
              }
              quiz[question.key] = val;
            }
            payload.quiz = quiz;
          }

          self.submit(payload, submitBtn, errorBox);
        },
      });
      step.appendChild(this.actions(submitBtn, [this.backLink(function () { self.renderReasonStep(); })]));
      this.appendChild(step);
    }

    getSelectedItems() {
      var self = this;
      return this.state.order.items.filter(function (_, i) { return self.state.selected[i]; });
    }

    renderDynamicArea(area) {
      var self = this;
      area.innerHTML = '';
      var selectedItems = this.getSelectedItems();

      var stylingItems = selectedItems.filter(function (item) {
        return item.category === 'styling' || item.category === 'treatment';
      });
      var hasWashing = selectedItems.some(function (item) { return item.category === 'washing'; });
      var hasOther = selectedItems.some(function (item) { return item.category === 'other'; });

      // Usage tips for styling/treatment products
      if (stylingItems.length > 0) {
        area.appendChild(el('h3', { class: 'hrf-heading', text: 'A quick tip before you decide' }));
        stylingItems.forEach(function (item) {
          if (!item.tip) return;
          var tip = el('div', { class: 'hrf-tip' });
          tip.appendChild(el('strong', { text: item.title }));
          tip.appendChild(document.createTextNode(item.tip));
          area.appendChild(tip);
        });
      }

      // Resolution offers
      if (stylingItems.length > 0 || hasOther) {
        area.appendChild(el('h3', { class: 'hrf-heading', text: 'How would you like to resolve this?' }));
        RESOLUTIONS.forEach(function (r) {
          area.appendChild(
            el('label', { class: 'hrf-option' }, [
              el('input', { type: 'radio', name: 'hrf-resolution', value: r.value }),
              el('span', { text: r.label }, [el('span', { class: 'hrf-option-detail', text: r.detail })]),
            ])
          );
        });
      }

      // New Wash quiz
      if (hasWashing) {
        area.appendChild(
          el('h3', { class: 'hrf-heading', text: 'Help us understand your New Wash experience' })
        );
        QUIZ_QUESTIONS.forEach(function (question) {
          var wrap = el('div', { class: 'hrf-quiz-q' });
          wrap.appendChild(el('label', { class: 'hrf-label', text: question.label }));
          if (question.type === 'select') {
            var select = el('select', { class: 'hrf-select', name: 'hrf-quiz-' + question.key });
            select.appendChild(el('option', { value: '', text: 'Select an answer…' }));
            question.options.forEach(function (opt) {
              select.appendChild(el('option', { value: opt, text: opt }));
            });
            wrap.appendChild(select);
          } else if (question.type === 'multi') {
            question.options.forEach(function (opt) {
              wrap.appendChild(
                el('label', { class: 'hrf-option' }, [
                  el('input', { type: 'checkbox', name: 'hrf-quiz-' + question.key, value: opt }),
                  el('span', { text: opt }),
                ])
              );
            });
          } else {
            wrap.appendChild(el('input', { class: 'hrf-input', type: 'text', name: 'hrf-quiz-' + question.key }));
          }
          area.appendChild(wrap);
        });
      }
    }

    // ---------- Submit + success ----------

    submit(payload, btn, errorBox) {
      var self = this;
      var origLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Submitting…';
      fetch(this.apiBase + '/api/returns/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok) {
            btn.disabled = false;
            btn.textContent = origLabel;
            errorBox.textContent = result.data.error || 'Something went wrong. Please try again.';
            return;
          }
          self.renderSuccess(payload.reason, Boolean(payload.photoPath));
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = origLabel;
          errorBox.textContent = 'Something went wrong. Please try again.';
        });
    }

    renderSuccess(reason, photoAttached) {
      this.innerHTML = '';
      var box = el('div', { class: 'hrf-success' }, [
        el('h3', { text: 'Request received!' }),
        el('p', { text: 'Our customer experience team will follow up with you by email shortly.' }),
      ]);
      if (PHOTO_NOTES[reason] && !photoAttached) {
        box.appendChild(
          el('div', {
            class: 'hrf-notice',
            text: 'Don’t forget: when we email you, please reply with ' + PHOTO_NOTES[reason] + '.',
          })
        );
      }
      this.appendChild(box);
    }

    backLink(handler) {
      var self = this;
      return el('button', {
        class: 'hrf-btn-link',
        type: 'button',
        text: '← Back',
        onclick: handler || function () { self.renderReasonStep(); },
      });
    }

    /**
     * Lay out a step's actions: the primary button on its own, with any
     * secondary links (e.g. "submit without a photo", "back") stacked beneath
     * so they never collide.
     */
    actions(primaryBtn, secondaryLinks) {
      var children = [primaryBtn];
      if (secondaryLinks && secondaryLinks.length) {
        children.push(el('div', { class: 'hrf-actions-secondary' }, secondaryLinks));
      }
      return el('div', { class: 'hrf-actions' }, children);
    }
  }

  if (!customElements.get('hairstory-returns-form')) {
    customElements.define('hairstory-returns-form', HairstoryReturnsForm);
  }
})();
